import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';
import HighLogo from '../HighLogo';
import logo from "../../assets/logo.png";
import './LoginAdmin.css';
import adminApi, { setAdminAccessToken } from '../../shared/api/AxiosAdmin';

type LoginResponse = {
  token?: string;
  user?: { _id: string; email: string; profilename?: string; admin?: boolean };
  twoFARequired?: boolean; 
};

const LoginAdmin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { next?: string } };
  const next = location.state?.next || '/admin';

  const { setToken: setAuthToken } = (useAuth?.() ?? {}) as any;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
  
    try {
      const { data } = await adminApi.post<LoginResponse>('/admin/auth/login', { email, password });
        
      if (data?.twoFARequired) {
        setError('Two-factor authentication required (not implemented here).');
        return;
      }
  
      if (!data?.token) {
        setError('Login response did not include a token.');
        return;
      }
  
      // ✅ store token for persistence
      localStorage.setItem('unyAdminToken', data.token);
      setAdminAccessToken(data.token);
  
      // ✅ optionally call admin context `refresh()` if needed
      navigate(next, { replace: true });
    } catch (err: any) {
      console.error('Admin login failed:', err);
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.error ||
        err?.message ||
        'Login failed.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };
  

  return (
    <div
      id="admin-login"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        background: 'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
        color: '#f9fafb',
        fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* simple top bar like StartPage (logo only) */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 25px',
          background: 'rgba(2, 6, 23, 0.9)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(148,163,184,0.2)',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          title="Back to home"
        >
          <HighLogo
    src={logo}
    style={{ width: 170, display: 'block' }}
    padding={0}
  />
        </div>

        <div
          style={{
            fontSize: 12,
            color: '#b1b8c7',
            border: '1px solid rgba(148,163,184,0.25)',
            padding: '6px 12px',
            borderRadius: 999,
            background: 'rgba(2,6,23,0.6)',
          }}
        >
          Admin
        </div>
      </header>

      {/* centered card */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            borderRadius: 24,
            border: '1px solid rgba(148,163,184,0.3)',
            background: 'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))',
            boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
            padding: '20px 20px 24px',
            position: 'relative',
          }}
        >
          {/* back arrow inside card */}
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label="Back"
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="25"
              height="25"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#b1b8c7"
              strokeWidth="2.0"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div style={{ textAlign: 'center', marginBottom: 18, marginTop: 12 }}>
            <h1
              style={{
                fontSize: 24,
                marginBottom: 6,
                letterSpacing: -0.03,
                color: '#b1b8c7',
              }}
            >
              Admin Login
            </h1>
            <p style={{ fontSize: 13, color: '#b1b8c7' }}>
              Sign in to manage the platform.
            </p>
          </div>

          {!!error && (
            <div
              style={{
                marginBottom: 10,
                padding: 10,
                borderRadius: 10,
                color: '#fed7aa',
                background: 'rgba(127,29,29,0.3)',
                border: '1px solid rgba(248,113,113,0.6)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <form
            onSubmit={onSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <label style={{ fontSize: 13, color: '#e5e7eb' }}>
              Email
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                style={inputStyle}
                required
              />
            </label>

            <label style={{ fontSize: 13, color: '#e5e7eb' }}>
              Password
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                style={inputStyle}
                required
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              style={{
                background: '#b1b8c7',
                color: '#020617',
                padding: '10px',
                marginTop: 4,
                border: 'none',
                cursor: busy ? 'default' : 'pointer',
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15,
                opacity: busy ? 0.8 : 1,
              }}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            {/* no forgot/register on admin login */}
            <div
              style={{
                height: 1,
                width: '100%',
                background: 'linear-gradient(to right, transparent, #1f2937, transparent)',
                margin: '12px 0 4px',
              }}
            />

            <div style={{ textAlign: 'center', fontSize: 11, color: '#6b7280' }}>
              Admin access only.
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  marginTop: 4,
  padding: '10px 12px',
  width: '100%',
  borderRadius: 10,
  border: '1px solid #4b5563',
  background: '#020617',
  color: '#e5e7eb',
  fontSize: 14,
  boxSizing: 'border-box',
};

export default LoginAdmin;
